import styles from './Reports.module.css';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import { ExpandMore } from '@mui/icons-material';

import React from 'react';

import { DateTime } from 'luxon';

import * as api from '../../api.js';
import { useAuth } from '../../AuthContext';

import { ShiftsReport } from './ShiftsReport';
import { PatrollersReport } from './PatrollersReport';
import { RolesReport } from './RolesReport';
import { HoursReport } from './HoursReport';
import { AttendanceReport } from './AttendanceReport';

export const Reports = () => {
    const [ loadingIds, setLoadingIds ] = React.useState([]);
    const [ eventsError, setEventsError ] = React.useState(null);
    const [ patrollersError, setPatrollersError ] = React.useState(null);
    const [ shiftsError, setShiftsError ] = React.useState(null);
    const [ events, setEvents ] = React.useState([]);
    const [ selectedIds, setSelectedIds ] = React.useState([]);
    const [ shiftsByEvent, setShiftsByEvent ] = React.useState({});
    const requestedShifts = React.useRef(new Set());
    const [ patrollers, setPatrollers ] = React.useState([]);
    const [ tabValue, setTabValue ] = React.useState(0);
    const [ showUnderstaffed, setShowUnderstaffed ] = React.useState(false);
    const [ showOverstaffed, setShowOverstaffed ] = React.useState(false);
    const [ showMissingSaturday, setShowMissingSaturday ] = React.useState(false);

    // Pagination state for each report
    const [ shiftsPage, setShiftsPage ] = React.useState(0);
    const [ shiftsRowsPerPage, setShiftsRowsPerPage ] = React.useState(25);
    const [ patrollersPage, setPatrollersPage ] = React.useState(0);
    const [ patrollersRowsPerPage, setPatrollersRowsPerPage ] = React.useState(25);
    const [ rolesPage, setRolesPage ] = React.useState(0);
    const [ rolesRowsPerPage, setRolesRowsPerPage ] = React.useState(25);
    const [ hoursPage, setHoursPage ] = React.useState(0);
    const [ hoursRowsPerPage, setHoursRowsPerPage ] = React.useState(25);

    const { token } = useAuth();

    const shiftsLoading = loadingIds.length > 0;
    // Ticked events in picker order; the reports cover all of them combined.
    const selectedEvents = React.useMemo(() => events.filter(ev => selectedIds.includes(ev.id)), [ events, selectedIds ]);
    const shifts = React.useMemo(
        () => selectedEvents.flatMap(ev => shiftsByEvent[ ev.id ] || []),
        [ shiftsByEvent, selectedEvents ]
    );

    // ProtectedRoutes only renders Reports once a token exists; starting from
    // !!token means a missing token never leaves a spinner up forever.
    const [ eventsLoading, setEventsLoading ] = React.useState(!!token);
    const [ patrollersLoading, setPatrollersLoading ] = React.useState(!!token);

    // Phase 1: Data Structure Optimization - Create efficient lookup maps
    const patrollerMap = React.useMemo(() => {
        return new Map(patrollers.map(p => [ p.id, p ]));
    }, [ patrollers ]);

    const shiftMap = React.useMemo(() => {
        return new Map(shifts.map(s => [ s.id, s ]));
    }, [ shifts ]);

    // Extract roles from shifts and create role map
    const roleMap = React.useMemo(() => {
        const roles = new Map();

        shifts.forEach(shift => {
            shift.roles?.forEach(role => {
                if (!roles.has(role.id)) {
                    roles.set(role.id, role);
                }
            });
        });

        return roles;
    }, [ shifts ]);

    // Create events map for O(1) event lookups
    const eventsMap = React.useMemo(() => {
        return new Map(events.map(e => [ e.id, e ]));
    }, [ events ]);

    // Memoize event options for dropdown to prevent recreation on every render
    const eventOptions = React.useMemo(() => {
        return events.map((event) => (
            <MenuItem key={ event.id } value={ event.id }>
                <Checkbox checked={ selectedIds.includes(event.id) } />
                <ListItemText primary={ `${ event.nameLong || event.nameShort } | ${ DateTime.fromISO(event.start).toFormat('MMM dd') } - ${ DateTime.fromISO(event.end).toFormat('MMM dd') }` } />
            </MenuItem>
        ));
    }, [ events, selectedIds ]);

    // Events and patrollers are independent requests. Each fires once per token
    // change and is never retried automatically (a failure shows an error once).
    React.useEffect(() => {
        if (!token) return;

        const fetchEvents = async () => {
            try {
                setEventsLoading(true);
                const result = await api.getEvents(token);

                if (Array.isArray(result)) {
                    setEvents(result);
                } else {
                    setEventsError(result?.error || 'Unable to load events. Please try again.');
                }
            } catch (e) {
                setEventsError(e.message);
            } finally {
                setEventsLoading(false);
            }
        };

        fetchEvents();
    }, [ token ]);

    const fetchPatrollers = React.useCallback(async () => {
        try {
            setPatrollersError(null);
            setPatrollersLoading(true);
            const result = await api.getPatrollers(token);

            if (Array.isArray(result)) {
                setPatrollers(result);
            } else {
                setPatrollersError(result?.error || 'Unable to load patrollers. Please try again.');
            }
        } catch (e) {
            setPatrollersError(e.message);
        } finally {
            setPatrollersLoading(false);
        }
    }, [ token ]);

    React.useEffect(() => {
        if (!token) return;

        fetchPatrollers();
    }, [ token, fetchPatrollers ]);

    // Called from the change handler only: one request per newly ticked event,
    // cached afterwards. A failure unticks the event; ticking again retries.
    const loadShifts = async (eventId) => {
        if (requestedShifts.current.has(eventId)) return;
        requestedShifts.current.add(eventId);
        setLoadingIds(ids => [ ...ids, eventId ]);

        const fail = (message) => {
            requestedShifts.current.delete(eventId);
            setSelectedIds(ids => ids.filter(id => id !== eventId));
            setShiftsError(message);
        };

        try {
            const shiftsData = await api.getShiftsByEvent(eventId, token);

            if (Array.isArray(shiftsData)) {
                setShiftsByEvent(prev => ({ ...prev, [ eventId ]: shiftsData }));
            } else {
                fail(shiftsData?.error || 'Unable to load shifts. Please try again.');
            }
        } catch (e) {
            fail(e.message);
        } finally {
            setLoadingIds(ids => ids.filter(id => id !== eventId));
        }
    };

    const handleEventChange = (event) => {
        const value = event.target.value;
        const ids = typeof value === 'string' ? value.split(',') : value;
        const added = ids.filter(id => !selectedIds.includes(id));

        setShiftsError(null);
        setSelectedIds(ids);
        added.forEach(loadShifts);
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Pagination handlers
    const handleShiftsPageChange = (event, newPage) => {
        setShiftsPage(newPage);
    };

    const handleShiftsRowsPerPageChange = (event) => {
        setShiftsRowsPerPage(parseInt(event.target.value, 10));
        setShiftsPage(0);
    };

    const handlePatrollersPageChange = (event, newPage) => {
        setPatrollersPage(newPage);
    };

    const handlePatrollersRowsPerPageChange = (event) => {
        setPatrollersRowsPerPage(parseInt(event.target.value, 10));
        setPatrollersPage(0);
    };

    const handleRolesPageChange = (event, newPage) => {
        setRolesPage(newPage);
    };

    const handleRolesRowsPerPageChange = (event) => {
        setRolesRowsPerPage(parseInt(event.target.value, 10));
        setRolesPage(0);
    };

    const handleHoursPageChange = (event, newPage) => {
        setHoursPage(newPage);
    };

    const handleHoursRowsPerPageChange = (event) => {
        setHoursRowsPerPage(parseInt(event.target.value, 10));
        setHoursPage(0);
    };

    const getPatrollerName = React.useCallback((id) => {
        const patroller = patrollerMap.get(id);

        return patroller ? `${ patroller.firstName } ${ patroller.lastName }` : id;
    }, [ patrollerMap ]);

    const getPatrollerNameFormatted = React.useCallback((id) => {
        const patroller = patrollerMap.get(id);

        return patroller ? `${ patroller.lastName }, ${ patroller.firstName }` : id;
    }, [ patrollerMap ]);

    const formatDateTime = React.useCallback((date, time) => {
        const dt = DateTime.fromMillis(parseInt(time));

        return dt.toFormat('MMM dd, yyyy hh:mm a');
    }, []);

    const isSaturdayShift = React.useCallback((shift) => {
        const dt = DateTime.fromMillis(parseInt(shift.start));

        return dt.weekday === 6; // Luxon: 1=Monday, 7=Sunday, so 6=Saturday
    }, []);

    // Phase 2: Computation Optimization - Pre-compute expensive operations
    const processedShifts = React.useMemo(() => {
        return shifts.map(shift => ({
            ...shift,
            dateTime: DateTime.fromMillis(parseInt(shift.start)),
            isSaturday: isSaturdayShift(shift),
            totalSlots: shift.slots?.reduce((total, slot) => total + (slot.count || 0), 0) || 0,
            assignedCount: shift.patrollers?.length || 0
        }));
    }, [ shifts ]);

    // Pre-compute Saturday shifts set for O(1) lookups
    const saturdayShiftIds = React.useMemo(() => {
        return new Set(processedShifts.filter(s => s.isSaturday).map(s => s.id));
    }, [ processedShifts ]);

    // Phase 3: Redundancy Elimination - Shared utility functions
    const filterShiftsByStaffing = (shifts, showUnderstaffed, showOverstaffed) => {
        return shifts.filter(shift => {
            const totalSlots = shift.totalSlots;
            const assignedCount = shift.assignedCount;
            const isUnderstaffed = assignedCount < totalSlots;
            const isOverstaffed = assignedCount > totalSlots;

            // If no filters active, show all
            if (!showUnderstaffed && !showOverstaffed) return true;

            // If both filters active, show understaffed OR overstaffed
            if (showUnderstaffed && showOverstaffed) {
                return isUnderstaffed || isOverstaffed;
            }

            // If only understaffed filter active
            if (showUnderstaffed) return isUnderstaffed;

            // If only overstaffed filter active
            if (showOverstaffed) return isOverstaffed;

            return true;
        });
    };

    const groupShiftsByDay = (shifts) => {
        const shiftsByDay = {};

        shifts.forEach(shift => {
            const dt = shift.dateTime;
            const dayKey = dt.toFormat('yyyy-MM-dd');
            const dayLabel = dt.toFormat('EEEE, MMMM dd, yyyy');

            if (!shiftsByDay[ dayKey ]) {
                shiftsByDay[ dayKey ] = {
                    label: dayLabel,
                    shifts: []
                };
            }
            shiftsByDay[ dayKey ].shifts.push(shift);
        });

        return shiftsByDay;
    };

    const getRoleName = React.useCallback((roleId, roles) => {
        // First try the roleMap for O(1) lookup
        const role = roleMap.get(roleId);

        if (role) return role.name || roleId;

        // Fallback to roles array if not found (for edge cases)
        const fallbackRole = roles?.find(r => r.id === roleId);

        return fallbackRole?.name || roleId;
    }, [ roleMap ]);

    // Pre-compute role staffing for all shifts (expensive operation)
    const roleStaffingCache = React.useMemo(() => {
        const cache = new Map();

        processedShifts.forEach(shift => {
            const roleStaffing = {};

            // Initialize with required counts from slots
            shift.slots?.forEach(slot => {
                const roleName = getRoleName(slot.role, shift.roles);

                roleStaffing[ roleName ] = {
                    required: slot.count || 0,
                    assigned: 0,
                    roleId: slot.role
                };
            });

            // Count assigned patrollers by role
            shift.patrollers?.forEach(patroller => {
                const roleName = getRoleName(patroller.role, shift.roles);

                if (roleStaffing[ roleName ]) {
                    roleStaffing[ roleName ].assigned++;
                } else {
                    // Handle patrollers assigned to roles not defined in slots
                    roleStaffing[ roleName ] = {
                        required: 0, // Unknown requirement
                        assigned: 1,
                        roleId: patroller.role
                    };
                }
            });

            cache.set(shift.id, roleStaffing);
        });

        return cache;
    }, [ processedShifts, getRoleName ]);

    const getRoleStaffing = (shift) => {
        return roleStaffingCache.get(shift.id) || {};
    };

    // Phase 7: Lazy Rendering Optimization - Only render active tab
    const activeReport = React.useMemo(() => {
        switch (tabValue) {
            case 0:
                return <ShiftsReport
                    processedShifts={ processedShifts }
                    showUnderstaffed={ showUnderstaffed }
                    showOverstaffed={ showOverstaffed }
                    shiftsPage={ shiftsPage }
                    shiftsRowsPerPage={ shiftsRowsPerPage }
                    onPageChange={ handleShiftsPageChange }
                    onRowsPerPageChange={ handleShiftsRowsPerPageChange }
                    patrollerMap={ patrollerMap }
                    roleMap={ roleMap }
                    getPatrollerName={ getPatrollerName }
                    getRoleName={ getRoleName }
                />;
            case 1:
                return <PatrollersReport
                    processedShifts={ processedShifts }
                    selectedEvents={ selectedEvents }
                    saturdayShiftIds={ saturdayShiftIds }
                    shiftMap={ shiftMap }
                    patrollerMap={ patrollerMap }
                    eventsMap={ eventsMap }
                    patrollersPage={ patrollersPage }
                    patrollersRowsPerPage={ patrollersRowsPerPage }
                    showUnderstaffed={ showUnderstaffed }
                    showOverstaffed={ showOverstaffed }
                    showMissingSaturday={ showMissingSaturday }
                    onPageChange={ handlePatrollersPageChange }
                    onRowsPerPageChange={ handlePatrollersRowsPerPageChange }
                    getPatrollerNameFormatted={ getPatrollerNameFormatted }
                    getRoleName={ getRoleName }
                    formatDateTime={ formatDateTime }
                />;
            case 2:
                return <RolesReport
                    processedShifts={ processedShifts }
                    showUnderstaffed={ showUnderstaffed }
                    showOverstaffed={ showOverstaffed }
                    rolesPage={ rolesPage }
                    rolesRowsPerPage={ rolesRowsPerPage }
                    getPatrollerName={ getPatrollerName }
                    getRoleName={ getRoleName }
                    getRoleStaffing={ getRoleStaffing }
                    onPageChange={ handleRolesPageChange }
                    onRowsPerPageChange={ handleRolesRowsPerPageChange }
                />;
            case 3:
                return <HoursReport
                    processedShifts={ processedShifts }
                    patrollerMap={ patrollerMap }
                    hoursPage={ hoursPage }
                    hoursRowsPerPage={ hoursRowsPerPage }
                    onPageChange={ handleHoursPageChange }
                    onRowsPerPageChange={ handleHoursRowsPerPageChange }
                    getPatrollerNameFormatted={ getPatrollerNameFormatted }
                />;
            case 4:
                return <AttendanceReport
                    events={ selectedEvents }
                    patrollers={ patrollers }
                    shiftsByEvent={ shiftsByEvent }
                />;
            default:
                return null;
        }
    }, [
        tabValue,
        processedShifts,
        showUnderstaffed,
        showOverstaffed,
        showMissingSaturday,
        patrollers,
        shiftsPage,
        shiftsRowsPerPage,
        patrollersPage,
        patrollersRowsPerPage,
        rolesPage,
        rolesRowsPerPage,
        hoursPage,
        hoursRowsPerPage,
        selectedEvents,
        shiftsByEvent,
        events,
        saturdayShiftIds,
        shiftMap,
        patrollerMap,
        eventsMap,
        handleShiftsPageChange,
        handleShiftsRowsPerPageChange,
        handlePatrollersPageChange,
        handlePatrollersRowsPerPageChange,
        handleRolesPageChange,
        handleRolesRowsPerPageChange,
        handleHoursPageChange,
        handleHoursRowsPerPageChange,
        getPatrollerName,
        getPatrollerNameFormatted,
        getRoleName,
        formatDateTime,
        getRoleStaffing
    ]);

    if (eventsLoading) {
        return <CircularProgress />;
    }

    return (
        <div className={ styles.reports }>
            <Typography variant="h4" gutterBottom>
                Reports
            </Typography>

            { eventsError && (
                <Alert variant='filled' severity="error" sx={ { mb: 1 } } onClose={ () => setEventsError(null) }>{ eventsError }</Alert>
            ) }

            { patrollersError && (
                <Alert variant='filled' severity="error" sx={ { mb: 1 } } 
                    action={
                        <Button color="inherit" size="small" disabled={ patrollersLoading } onClick={ fetchPatrollers }>
                            Retry
                        </Button>
                    }
                >
                    Could not load patrollers: { patrollersError }
                </Alert>
            ) }

            { shiftsError && (
                <Alert variant='filled' severity="error" sx={ { mb: 1 } } onClose={ () => setShiftsError(null) }>{ shiftsError }</Alert>
            ) }

            <FormControl fullWidth margin="normal">
                <InputLabel>Select Events</InputLabel>

                <Select
                    multiple
                    value={ selectedIds }
                    onChange={ handleEventChange }
                    label="Select Events"
                    renderValue={ (ids) => `${ ids.length } event${ ids.length === 1 ? '' : 's' } selected` }
                >
                    { eventOptions }
                </Select>
            </FormControl>

            { selectedEvents.length > 0 && (
                <Box sx={ { width: '100%', mt: 2 } }>
                    { selectedEvents.map((selectedEventData) => {
                        return (
                            <Accordion key={ selectedEventData.id } sx={{ mb: 2 }} >
                                <AccordionSummary expandIcon={ <ExpandMore /> }>
                                    <Typography variant="h6">
                                        Event Details{ selectedEvents.length > 1 ? `: ${ selectedEventData.nameLong || selectedEventData.nameShort }` : '' }
                                    </Typography>
                                </AccordionSummary>

                                <AccordionDetails>
                                    { selectedEventData.description && (
                                        <Typography>
                                            { selectedEventData.description }
                                        </Typography>
                                    ) }

                                    <Typography color="text.secondary" sx={ { mt: 2 } }>
                                        { DateTime.fromISO(selectedEventData.start).toFormat('EEEE, MMMM dd, yyyy') } - { DateTime.fromISO(selectedEventData.end).toFormat('EEEE, MMMM dd, yyyy') }
                                    </Typography>

                                    <Divider sx={ { my: 1 } } />

                                    { selectedEventData.location && (
                                        <Typography variant="body2" sx={ { mt: 1 } }>
                                            <strong>Location:</strong> { selectedEventData.location }
                                        </Typography>
                                    ) }

                                    { selectedEventData.limit && (
                                        <Typography variant="body2" sx={ { mt: 1 } }>
                                            <strong>Required Shifts:</strong> { selectedEventData.limit }
                                        </Typography>
                                    ) }

                                    { selectedEventData.organizer && (
                                        <Typography variant="body2" sx={ { mt: 1 } }>
                                            <strong>Contact:</strong> { selectedEventData.organizer.name }
                                        </Typography>
                                    ) }

                                    <Divider sx={ { my: 1 } } />

                                    { selectedEventData.opens ? (
                                        <Typography variant="body2" sx={ { mt: 1 } }>
                                            <strong>General Registration Opens:</strong> { DateTime.fromISO(selectedEventData.opens).toFormat('MMM dd, yyyy hh:mm a') }
                                        </Typography>
                                    ) :
                                        <Typography variant="body2" sx={ { mt: 1 } }>
                                            <strong>Registration is currently open</strong> N/A
                                        </Typography>
                                    }

                                    { selectedEventData.closes && (
                                        <Typography variant="body2" sx={ { mt: 1 } }>
                                            <strong>General Registration Closes:</strong> { DateTime.fromISO(selectedEventData.closes).toFormat('MMM dd, yyyy hh:mm a') }
                                        </Typography>
                                    ) }

                                    <Divider sx={ { my: 1 } } />

                                    { selectedEventData.earlyOpen && (
                                        <Typography variant="body2" sx={ { mt: 1 } }>
                                            <strong>Early Registration Opens:</strong> { DateTime.fromISO(selectedEventData.earlyOpen).toFormat('MMM dd, yyyy hh:mm a') }
                                        </Typography>
                                    ) }

                                    { selectedEventData.earlyClose && (
                                        <Typography variant="body2" sx={ { mt: 1 } }>
                                            <strong>Early Registration Closes:</strong> { DateTime.fromISO(selectedEventData.earlyClose).toFormat('MMM dd, yyyy hh:mm a') }
                                        </Typography>
                                    ) }
                                </AccordionDetails>
                            </Accordion>
                        );
                    }) }

                    <Tabs value={ tabValue } onChange={ handleTabChange }>
                        <Tab label="Report by Shifts" />
                        <Tab label="Report by Patrollers" />
                        <Tab label="Report by Roles" />
                        <Tab label="Report by Hours" />
                        <Tab label="Attendance" />
                    </Tabs>

                    { (tabValue === 0 || tabValue === 1 || tabValue === 2) && (
                        <FormGroup row sx={ { mt: 2, mb: 1 } }>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={ showUnderstaffed }
                                        onChange={ (e) => setShowUnderstaffed(e.target.checked) }
                                        color="warning"
                                    />
                                }
                                label="Show Understaffed Only"
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={ showOverstaffed }
                                        onChange={ (e) => setShowOverstaffed(e.target.checked) }
                                        color="error"
                                    />
                                }
                                label="Show Overstaffed Only"
                            />

                            { tabValue === 1 && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={ showMissingSaturday }
                                            onChange={ (e) => setShowMissingSaturday(e.target.checked) }
                                            color="info"
                                        />
                                    }
                                    label="Show Missing Saturday Only"
                                />
                            ) }
                        </FormGroup>
                    ) }
                    <Box sx={ { mt: 2 } }>
                        { (shiftsLoading || patrollersLoading) ? (
                            <Box sx={ { display: 'flex', alignItems: 'center', gap: 2 } }>
                                <CircularProgress size={ 24 } />
                                <Typography>{ patrollersLoading ? 'Loading patrollers...' : 'Loading shifts...' }</Typography>
                            </Box>
                        ) : (!patrollersError && activeReport) }
                    </Box>
                </Box>
            ) }
        </div>
    );
};
